import { useState, useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { CalendarIcon, RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { format, subDays } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts'
import { DateRange } from 'react-day-picker'
import {
  usePlaybackDurationRecords,
  usePlaybackDurationStats,
} from '@/hooks/usePlaybackDuration'

interface Props {
  broadcastDeviceId: number
}

// 秒 → "H 小時 M 分" 顯示格式
function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0 分'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0) return `${m} 分`
  if (m === 0) return `${h} 小時`
  return `${h} 小時 ${m} 分`
}

// 秒 → 小時（取 1 位小數），給圖表用
function secondsToHours(seconds: number): number {
  return Math.round((seconds / 3600) * 10) / 10
}

export default function BroadcastDevicePlaybackDurationInfo({ broadcastDeviceId }: Props) {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 7),
    to: new Date(),
  })
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d')

  const handleTimeRangeChange = (range: '7d' | '30d' | '90d') => {
    setTimeRange(range)
    const today = new Date()
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90
    setDateRange({ from: subDays(today, days), to: today })
  }

  const recordsParams = useMemo(
    () => ({
      broadcastDeviceId,
      startDate: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    }),
    [broadcastDeviceId, dateRange],
  )

  const { records, isLoading: isRecordsLoading, mutate: refreshRecords } =
    usePlaybackDurationRecords(recordsParams)

  const { stats, isLoading: isStatsLoading } = usePlaybackDurationStats(broadcastDeviceId)

  const chartData = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return []

    const allDates: Date[] = []
    const cur = new Date(dateRange.from)
    const end = new Date(dateRange.to)
    const maxDays = 90
    let cnt = 0
    while (cur <= end && cnt < maxDays) {
      allDates.push(new Date(cur))
      cur.setDate(cur.getDate() + 1)
      cnt++
    }

    const map = new Map<string, number>()
    records.forEach((rec) => {
      map.set(rec.date, rec.duration_seconds)
    })

    return allDates.map((d) => {
      const dstr = format(d, 'yyyy-MM-dd')
      const sec = map.get(dstr) || 0
      return {
        date: format(d, 'MM/dd'),
        fullDate: dstr,
        hours: secondsToHours(sec),
        seconds: sec,
      }
    })
  }, [dateRange, records])

  const growthRate = useMemo(() => {
    if (!stats || stats.yesterday === 0) return 0
    return ((stats.today - stats.yesterday) / stats.yesterday) * 100
  }, [stats])

  const getTrendIcon = () => {
    if (growthRate > 0) return <TrendingUp className="h-4 w-4 text-green-500" />
    if (growthRate < 0) return <TrendingDown className="h-4 w-4 text-red-500" />
    return <Minus className="h-4 w-4 text-gray-400" />
  }

  const getTrendColor = () => {
    if (growthRate > 0) return 'text-green-600'
    if (growthRate < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  return (
    <div className="space-y-6">
      {/* 今日 / 昨日 / 成長率 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">今日播放時長</CardTitle>
          </CardHeader>
          <CardContent>
            {isStatsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-gray-900">
                {formatDuration(stats?.today || 0)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">昨日播放時長</CardTitle>
          </CardHeader>
          <CardContent>
            {isStatsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-gray-700">
                {formatDuration(stats?.yesterday || 0)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">日成長率</CardTitle>
          </CardHeader>
          <CardContent>
            {isStatsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-center gap-2">
                {getTrendIcon()}
                <span className={`text-xl font-bold ${getTrendColor()}`}>
                  {Math.abs(growthRate).toFixed(1)}%
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 每日播放時長圖表 */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">裝置播放時長統計</CardTitle>
          <CardDescription>
            依 WebSocket 連線狀態累計，每日 00:00（台北時間）重新起算
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Select
                value={timeRange}
                onValueChange={(v: string) =>
                  handleTimeRangeChange(v as '7d' | '30d' | '90d')
                }
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">過去7天</SelectItem>
                  <SelectItem value="30d">過去30天</SelectItem>
                  <SelectItem value="90d">過去90天</SelectItem>
                </SelectContent>
              </Select>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[240px] justify-start text-left">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from
                      ? dateRange.to
                        ? `${format(dateRange.from, 'yyyy-MM-dd')} - ${format(
                            dateRange.to,
                            'yyyy-MM-dd',
                          )}`
                        : format(dateRange.from, 'yyyy-MM-dd')
                      : '選擇日期範圍'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-auto p-0 bg-popover">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(rng) => {
                      if (rng?.from) {
                        setDateRange({ from: rng.from, to: rng.to || rng.from })
                        if (rng.to || rng.from) setCalendarOpen(false)
                      }
                    }}
                    numberOfMonths={2}
                    locale={zhTW}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button variant="outline" size="icon" onClick={() => refreshRecords()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-4">
            {isRecordsLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : chartData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                暫無數據
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis
                    label={{ value: '小時', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip
                    formatter={(
                      _value: number,
                      _name: string,
                      item: { payload?: { seconds?: number } },
                    ) => [formatDuration(item.payload?.seconds ?? 0), '播放時長']}
                  />
                  <Legend />
                  <Bar dataKey="hours" name="播放時長（小時）" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>平均每日時長</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {chartData.length > 0
                    ? formatDuration(
                        Math.round(
                          chartData.reduce((s, i) => s + i.seconds, 0) / chartData.length,
                        ),
                      )
                    : '0 分'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>最長日時長</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {chartData.length > 0
                    ? formatDuration(Math.max(...chartData.map((i) => i.seconds)))
                    : '0 分'}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>總計時長</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(chartData.reduce((s, i) => s + i.seconds, 0))}
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
